import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight, MapPin } from 'lucide-react';
import axios from 'axios';
import { API, IMG } from '../constants/site';
import { resolveImg } from '../constants/siteImages';
import { useLanguage } from '../i18n/LanguageContext';
import { useSiteImage } from '../components/SiteImages';
import { useSEO } from '../components/SEO';
import { Reveal, MaskedTitle, Marquee, Overline, EASE } from '../components/motion';
import { LeadForm } from '../components/LeadForm';
import { TestimonialStrip, Testimonials } from '../components/Testimonials';

const Hero = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroImg = useSiteImage('hero.home', IMG.hero);

  return (
    <section data-testid="hero-section" className="relative min-h-svh flex items-center overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-40">
        <motion.img
          src={heroImg}
          alt="Domidoor Solutions"
          className="w-full h-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: 'easeOut' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-black/20" />
      </motion.div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full pt-32 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}>
            <Overline className="mb-8 flex items-center gap-3">
              <span className="w-8 h-px bg-accent" /> {t('hero.overline')}
            </Overline>
          </motion.div>
          <MaskedTitle
            lines={[t('hero.l1'), t('hero.l2'), t('hero.l3')]}
            className="font-display text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tight leading-[1.02] mb-8 text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="text-white/75 text-base md:text-lg max-w-xl leading-relaxed mb-10"
          >
            {t('hero.sub')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="flex flex-wrap gap-4"
          >
            <a
              href="#hero-form"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 w-full sm:w-auto hover:bg-accenth transition-colors duration-300"
            >
              {t('hero.cta1')} <ArrowRight size={16} />
            </a>
            <Link
              to="/configurator"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 w-full sm:w-auto hover:border-accent hover:bg-white/10 transition-all duration-300"
            >
              {t('configurator.heroBtn')}
            </Link>
          </motion.div>
        </div>

        <motion.div
          id="hero-form"
          data-testid="hero-form-panel"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          className="lg:col-span-5 force-light bg-white border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-10 scroll-mt-32 shadow-[0_30px_90px_rgba(0,0,0,0.25)]"
        >
          <p className="font-display text-2xl font-medium mb-1">{t('hero.formTitle')}</p>
          <p className="text-smoke text-sm mb-8 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" /> {t('hero.formSub')}
          </p>
          <LeadForm testIdPrefix="lead-form" />
        </motion.div>
      </div>
    </section>
  );
};

const USP = () => {
  const { t } = useLanguage();
  const items = t('usp.items');
  return (
    <section data-testid="usp-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <Reveal className="md:col-span-4">
          <Overline>{t('usp.overline')}</Overline>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-8">
          <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight">{t('usp.title')}</h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-line/10">
        {items.map((item, i) => (
          <Reveal key={i} delay={i * 0.08} y={24}>
            <div data-testid={`usp-item-${i}`} className="bg-paper p-5 md:p-10 h-full group hover:bg-surface transition-colors duration-500">
              <span className="font-display text-sm text-accent tracking-[0.2em]">0{i + 1}</span>
              <h3 className="font-display text-base md:text-xl font-medium mt-4 md:mt-6 mb-2 md:mb-3 group-hover:text-accent transition-colors duration-300">{item.t}</h3>
              <p className="text-smoke text-xs md:text-sm leading-relaxed">{item.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const CHAPTER_IMAGES = { pvc: '/images/chapter-pvc.jpg', garaj: '/images/chapter-garaj.jpg', auto: '/images/chapter-auto.jpg', rulouri: IMG.house2 };
const CHAPTER_LINKS = { pvc: '/tamplarie-pvc', garaj: '/porti-de-garaj', auto: '/automatizari', rulouri: '/rulouri' };

const Chapters = () => {
  const { t } = useLanguage();
  const chapterImgs = {
    pvc: useSiteImage('home.chapter.pvc', CHAPTER_IMAGES.pvc),
    garaj: useSiteImage('home.chapter.garaj', CHAPTER_IMAGES.garaj),
    auto: useSiteImage('home.chapter.auto', CHAPTER_IMAGES.auto),
    rulouri: useSiteImage('home.chapter.rulouri', CHAPTER_IMAGES.rulouri),
  };
  const keys = ['pvc', 'garaj', 'auto', 'rulouri'];
  return (
    <section data-testid="chapters-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 space-y-28 md:space-y-40">
      {keys.map((key, idx) => {
        const ch = t(`chapters.${key}`);
        const flip = idx % 2 === 1;
        return (
          <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
            <Reveal className={`md:col-span-7 ${flip ? 'md:order-2' : ''}`}>
              <Link to={CHAPTER_LINKS[key]} data-testid={`chapter-image-${key}`} className="block relative overflow-hidden group">
                <img
                  src={chapterImgs[key]}
                  alt={ch.title}
                  loading="lazy"
                  decoding="async"
                  className={`w-full h-[320px] md:h-[480px] object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 ${flip ? 'clip-frame-alt' : 'clip-frame'}`}
                />
                <span className="absolute inset-0 bg-black/25 group-hover:bg-transparent transition-colors duration-700" />
                <span className="absolute bottom-6 right-6 w-12 h-12 grid place-items-center bg-accent text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight size={20} />
                </span>
              </Link>
            </Reveal>
            <div className={`md:col-span-5 ${flip ? 'md:order-1' : ''}`}>
              <Reveal>
                <span className="font-display text-6xl md:text-7xl font-medium text-outline block mb-6">{ch.num}</span>
                <Overline className="mb-4">{t('chapters.overline')}</Overline>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-5">{ch.title}</h2>
                <p className="text-smoke leading-relaxed mb-8">{ch.desc}</p>
                <Link
                  to={CHAPTER_LINKS[key]}
                  data-testid={`chapter-cta-${key}`}
                  className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-accent border-b border-accent/40 pb-2 hover:gap-5 hover:border-accent transition-all duration-300"
                >
                  {t('chapters.cta')} <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const Why = () => {
  const { t } = useLanguage();
  const points = t('why.points');
  const stats = t('why.stats');
  return (
    <section data-testid="why-section" className="bg-surface2 border-y border-line/10">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline className="mb-6">{t('why.overline')}</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight leading-tight mb-12">{t('why.title')}</h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-x-8 gap-y-10">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`stat-${i}`} className="border-l-2 border-accent pl-5">
                  <span className="font-display text-4xl md:text-5xl font-medium">{s.v}</span>
                  <p className="text-smoke text-xs uppercase tracking-[0.15em] mt-2">{s.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-ink/10 border border-line/10 self-center">
          {points.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`why-point-${i}`} className="bg-surface2 p-8 md:p-10 h-full hover:bg-surface transition-colors duration-500">
                <h3 className="font-display text-lg font-medium mb-3 text-accent">{p.t}</h3>
                <p className="text-smoke text-sm leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Brands = () => {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    axios.get(`${API}/brands`).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  return (
    <section data-testid="brands-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <Overline className="mb-4">{t('brands.overline')}</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('brands.title')}</h2>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-ink/10 border border-line/10">
        {brands.map((b, i) => (
          <Reveal key={b.id || i} delay={i * 0.05}>
            <div
              data-testid={`brand-${i}`}
              className="bg-paper h-28 md:h-32 grid place-items-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
            >
              <span className="font-display text-xl md:text-2xl font-semibold tracking-wide text-ink">{b.name}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const PortfolioPreview = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/portfolio`).then((r) => setItems(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section data-testid="portfolio-preview-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Overline className="mb-4">{t('portfolioHome.overline')}</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('portfolioHome.title')}</h2>
        </div>
        <Link
          to="/portofoliu"
          data-testid="portfolio-preview-cta"
          className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-accent border-b border-accent/40 pb-2 hover:gap-5 hover:border-accent transition-all duration-300"
        >
          {t('portfolioHome.cta')} <ArrowRight size={15} />
        </Link>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {items.map((item, i) => (
          <Reveal key={item.id || i} delay={i * 0.1} className={i === 0 ? 'md:col-span-7' : i === 1 ? 'md:col-span-5' : 'md:col-span-12'}>
            <Link to="/portofoliu" data-testid={`portfolio-preview-item-${i}`} className="block relative overflow-hidden group">
              <img
                src={resolveImg((item.images && item.images[0]) || item.image)}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className={`w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${i === 2 ? 'h-64 md:h-80' : 'h-72 md:h-96'}`}
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
              <span className="absolute bottom-6 left-6 right-6 text-white">
                <span className="font-display text-xl font-medium block">{item.title}</span>
                <span className="text-smoke text-xs uppercase tracking-[0.15em] flex items-center gap-2 mt-2">
                  <MapPin size={13} className="text-accent" /> {item.location}
                </span>
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Area = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="area-section" className="relative overflow-hidden border-y border-line/10">
      <img src="/images/area-map.jpg" alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-35 grayscale" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36">
        <Reveal>
          <Overline className="mb-6">{t('area.overline')}</Overline>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tight mb-6 max-w-2xl text-white">{t('area.title')}</h2>
          <p className="text-white/75 max-w-xl leading-relaxed mb-10">{t('area.desc')}</p>
          <p data-testid="area-places" className="text-sm md:text-base text-white/70 tracking-wide border-l-2 border-accent pl-5">
            {t('area.places')}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="final-cta-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36 text-left">
      <Reveal>
        <h2 className="font-display text-4xl md:text-7xl font-medium tracking-tight leading-[1.02] mb-6 max-w-4xl">
          {t('finalCta.title')}
        </h2>
        <p className="text-smoke text-lg max-w-xl mb-10">{t('finalCta.sub')}</p>
        <Link
          to="/contact"
          data-testid="final-cta-button"
          className="inline-flex items-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-10 py-5 hover:bg-accenth transition-colors duration-300"
        >
          {t('finalCta.btn')} <ArrowRight size={16} />
        </Link>
      </Reveal>
    </section>
  );
};

export default function Home() {
  const { t } = useLanguage();
  useSEO('home');
  return (
    <main data-testid="home-page">
      <Hero />
      <TestimonialStrip />
      <Marquee text={t('marquee')} />
      <USP />
      <Chapters />
      <Why />
      <Brands />
      <PortfolioPreview />
      <Area />
      <Testimonials />
      <FinalCTA />
    </main>
  );
}
